<script setup lang="ts">
export interface BodyItem {
  color?: string;
  title: string;
  value: string;
  loading?: boolean;
}

export interface TooltipData {
  title?: string;
  subtitle?: string;
  body?: BodyItem[];
  footer?: string;
  description?: string;
}

defineProps<
  TooltipData & {
    isBelow?: boolean;
    noArrow?: boolean;
    arrowPosition?: 'top' | 'bottom' | 'left' | 'right';
  }
>();
</script>

<template>
  <div>
    <div class="relative transition-all duration-200 ease-out">
      <div class="bg-gray-800 p-2 rounded-lg shadow-lg min-w-[200px] pb-0" :class="footer ? 'pb-2' : 'pb-0'">
        <!-- Title Section -->
        <div v-if="title || subtitle" class="mb-1 border-b border-gray-700 py-2">
          <div class="text-gray-200 text-xs">{{ title }}</div>
          <div class="text-lg text-white font-semibold">{{ subtitle }}</div>
        </div>

        <!-- Body Section -->
        <div v-if="body" class="space-y-2 text-xs border-b border-gray-700 py-2">
          <div v-for="(item, index) in body" :key="index" class="flex justify-between items-center">
            <div class="flex items-center gap-[10px] mr-2">
              <div v-if="item.color" class="w-2 h-2 rounded-[2px]" :style="{ backgroundColor: item.color }"></div>
              <span class="text-gray-400 truncate">{{ item.title }}</span>
            </div>
            <ui-skeleton-loader v-if="item.loading" class="w-[80px] h-[12px] rounded-md" />
            <span v-else class="text-white font-semibold">{{ item.value }}</span>
          </div>
        </div>

        <!-- Date Section -->
        <div
          class="flex items-center justify-center text-gray-400 text-[10px] text-center px-2"
          :class="description ? 'h-[24px]' : 'h-[30px]'"
        >
          {{ footer }}
        </div>
        <div v-if="description" class="flex items-center justify-center text-gray-400 text-[10px] text-center">{{ description }}</div>
      </div>

      <!-- Triangle Pointer -->
      <div
        v-if="!noArrow"
        class="absolute transition-all duration-200 ease-out"
        :class="[
          // Vertical arrows (top/bottom)
          {
            'w-0 h-0 border-l-[8px] border-l-transparent border-r-[8px] border-r-transparent':
              !arrowPosition || arrowPosition === 'top' || arrowPosition === 'bottom',
            '-bottom-[6px] border-t-[8px] border-t-gray-800 border-b-0': (!arrowPosition && !isBelow) || arrowPosition === 'bottom',
            '-top-[6px] border-b-[8px] border-b-gray-800 border-t-0': (!arrowPosition && isBelow) || arrowPosition === 'top',
          },
          // Horizontal arrows (left/right)
          {
            'h-0 w-0 border-t-[8px] border-t-transparent border-b-[8px] border-b-transparent':
              arrowPosition === 'left' || arrowPosition === 'right',
            '-right-[6px] border-l-[8px] border-l-gray-800 border-r-0': arrowPosition === 'right',
            '-left-[6px] border-r-[8px] border-r-gray-800 border-l-0': arrowPosition === 'left',
            'top-1/2 -translate-y-1/2': arrowPosition === 'left' || arrowPosition === 'right',
          },
        ]"
        :style="{
          left: arrowPosition === 'left' || arrowPosition === 'right' ? undefined : 'var(--triangle-left, 50%)',
          transform:
            arrowPosition === 'left' || arrowPosition === 'right' ? 'translateY(-50%)' : 'var(--triangle-transform, translateX(-50%))',
        }"
      ></div>
    </div>
  </div>
</template>

<style>
:root {
  --triangle-left: 50%;
  --triangle-transform: translateX(-50%);
}

#chart-tooltip {
  transition: all 0.2s ease-out;
  opacity: 0;
  animation: fadeIn 0.2s ease-out forwards;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-5px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
